import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Vault, Plus, Trash, PencilSimple, Eye, EyeSlash, Copy } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/pages/command/ui";

const TYPES = [["vendor", "Vendor"], ["bank", "Bank Account"], ["email", "Email"], ["credit_app", "Credit App Login"], ["other", "Other"]];
const TIERS = ["Tier 1", "Tier 2", "Tier 3"];
const empty = (clientId, companyId) => ({ client_id: clientId || null, company_id: companyId || null, type: "vendor", label: "", url: "", username: "", password: "", tier: "none", signup_date: "", payment_due: "", notes: "" });

export function AccountsVault({ clientId, companyId, showTier = false }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty(clientId, companyId));
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState({});

  const load = useCallback(async () => {
    const params = {}; if (clientId) params.client_id = clientId; if (companyId) params.company_id = companyId;
    try { const { data } = await api.get("/cc/accounts", { params }); setRows(data); } catch { /* ignore */ }
  }, [clientId, companyId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditId(null); setForm(empty(clientId, companyId)); setOpen(true); };
  const openEdit = (a) => { setEditId(a.id); setForm({ ...empty(clientId, companyId), ...a, tier: a.tier || "none" }); setOpen(true); };
  const save = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    setSaving(true);
    try {
      const payload = { ...form, client_id: clientId || null, company_id: companyId || null, tier: form.tier === "none" ? null : form.tier };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editId) await api.put(`/cc/accounts/${editId}`, payload);
      else await api.post("/cc/accounts", payload);
      toast.success("Saved"); setOpen(false); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (a) => { if (!window.confirm(`Delete "${a.label}"?`)) return; try { await api.delete(`/cc/accounts/${a.id}`); setRows((r) => r.filter((x) => x.id !== a.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const copy = (t) => { navigator.clipboard?.writeText(t || ""); toast.success("Copied"); };
  const typeLabel = (t) => (TYPES.find(([v]) => v === t) || [null, t])[1];

  return (
    <div data-testid="accounts-vault">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Vault size={20} className="text-primary" /><h2 className="text-lg font-medium">Accounts &amp; Logins ({rows.length})</h2></div>
        <Button size="sm" onClick={openNew} data-testid="new-account-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={16} weight="bold" /> Add account</Button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-muted-foreground">No stored accounts yet. Add vendor, bank, email or credit-app logins.</div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="accounts-list">
          {rows.map((a) => (
            <div key={a.id} className="px-4 py-3.5" data-testid={`account-row-${a.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.label} <span className="text-xs text-muted-foreground">· {typeLabel(a.type)}{a.tier ? ` · ${a.tier}` : ""}</span></div>
                  <div className="text-xs text-muted-foreground truncate">
                    {a.username ? `user: ${a.username}` : ""}{a.url ? ` · ${a.url}` : ""}
                    {a.payment_due ? ` · due ${fmtDate(a.payment_due)}` : ""}{a.signup_date ? ` · since ${fmtDate(a.signup_date)}` : ""}
                  </div>
                  {a.password ? (
                    <div className="text-xs mt-1 flex items-center gap-2">
                      <span className="text-muted-foreground">pass:</span>
                      <span className="font-mono">{reveal[a.id] ? a.password : "••••••••"}</span>
                      <button onClick={() => setReveal((r) => ({ ...r, [a.id]: !r[a.id] }))} data-testid={`reveal-${a.id}`} className="text-primary">{reveal[a.id] ? <EyeSlash size={14} /> : <Eye size={14} />}</button>
                      <button onClick={() => copy(a.password)} className="text-primary"><Copy size={14} /></button>
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`delete-account-${a.id}`} className="text-red-300 hover:text-red-200"><Trash size={16} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Account" : "Add Account"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Credentials are encrypted at rest.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Label *</Label><Input data-testid="account-label-input" value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Uline, Chase Ink" /></div>
              <div className="col-span-2"><Label>URL</Label><Input value={form.url} onChange={(e) => set("url", e.target.value)} /></div>
              <div><Label>Username</Label><Input data-testid="account-username-input" value={form.username} onChange={(e) => set("username", e.target.value)} /></div>
              <div><Label>Password</Label><Input data-testid="account-password-input" value={form.password} onChange={(e) => set("password", e.target.value)} /></div>
              {showTier && (
                <div>
                  <Label>Tier</Label>
                  <Select value={form.tier} onValueChange={(v) => set("tier", v)}>
                    <SelectTrigger data-testid="account-tier-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div><Label>Signed up</Label><Input type="date" value={form.signup_date || ""} onChange={(e) => set("signup_date", e.target.value)} /></div>
              <div><Label>Payment due</Label><Input type="date" value={form.payment_due || ""} onChange={(e) => set("payment_due", e.target.value)} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-account-button" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
